import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';

function AssignModal({ claim, onClose, onSuccess, S }) {
  const { t } = useTranslation();
  const [selectedAdjuster, setSelectedAdjuster] = useState('');
  const [loading, setLoading] = useState(false);
  const [adjusters, setAdjusters] = useState([]);

  React.useEffect(() => {
    // Fetch every user role that can be assigned a claim. Prior to the
    // T-10.7 fix this only queried role=adjuster, so seniorAdjuster /
    // claimsManager / fieldAdjuster users were silently missing from the
    // dropdown. Reuse the /claims/assignees endpoint the list screen
    // already uses — it applies the same tenant scope and role filter.
    const fetchAdjusters = async () => {
      try {
        const resp = await axios.get(`${process.env.REACT_APP_BASE_URL}/claims/assignees`, { withCredentials: true });
        const list = resp.data?.data || [];
        // /claims/assignees returns { id, name, role }. Normalize to the
        // shape the <option> mapper expects (needs _id + name).
        setAdjusters(list.map(u => ({ _id: u.id || u._id, name: u.name })));
      } catch (err) {
        toast.error(t('screens.claimsScreen.detail.loadAdjustersFailed'));
      }
    };
    fetchAdjusters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAssign = async () => {
    if (!selectedAdjuster) {
      toast.error(t('screens.claimsScreen.detail.selectAnAdjusterError'));
      return;
    }
    setLoading(true);
    try {
      await axios.patch(`${process.env.REACT_APP_BASE_URL}/claims/${claim._id}/assign`, { assignedTo: selectedAdjuster }, { withCredentials: true });
      toast.success(t('screens.claimsScreen.detail.assignSuccess'));
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || t('screens.claimsScreen.detail.assignFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!claim) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 400, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.claimsScreen.detail.assignModalTitle')}</div>
        <div style={{ fontSize: 13, color: S.textMuted, marginBottom: 18 }}>{t('screens.claimsScreen.detail.assignModalDesc')}</div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textSecondary, marginBottom: 8 }}>{t('screens.claimsScreen.detail.adjuster')}</label>
          <select
            value={selectedAdjuster}
            onChange={e => setSelectedAdjuster(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, outline: 'none' }}
          >
            <option value=''>{t('screens.claimsScreen.detail.selectAnAdjuster')}</option>
            {adjusters.map(adj => (
              <option key={adj._id} value={adj._id + ',' + adj.name}>
                {adj.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              color: S.textSecondary,
            }}
          >
            {t('screens.claimsScreen.detail.cancel')}
          </button>
          <button
            onClick={handleAssign}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: S.burgundy,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t('screens.claimsScreen.detail.assigning') : t('screens.claimsScreen.detail.assign')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssignModal;
